// TRIAGE-01 (audit decision 3.2 / IMP-01) — the user-configurable triage
// keymap model.
//
// Pure data + pure functions, no React. The hotkey layer
// (`useTriageHotkeys`) resolves incoming `KeyboardEvent`s against the
// active keymap; the Settings → Shortcuts tab rebinds entries through
// `rebindTriageKey` (capture-next-keypress pattern).
//
// Defaults (Christian, 2026-06-11 — QUESTIONS.md Q1 decided block):
//   j / k            next / previous finding on the active image
//   a AND Enter      accept (promote) the active finding
//   x AND Delete     dismiss the active finding instantly (undoable)
//   Shift+X          dismiss with reason (popover with preset chips)
//   Space            next data point WITH pending findings (effective
//                    filtered set — Q8 decided)
//   Shift+Space      next UNSEEN data point
//   1..5             severity grade assignment (defect selections)
//
// The aerios-iq demo bindings are explicitly IGNORED (Q1 decided).
//
// Chord format: `[Shift+]<Key>` where <Key> is a normalised
// `KeyboardEvent.key` — single letters lowercased, `" "` spelled
// `Space`. Ctrl/Cmd/Alt chords are NOT triage chords (reserved for the
// browser + the undo/redo layer) — `eventToChord` returns `null` for
// them so they can never be bound nor dispatched here.

export const TRIAGE_ACTIONS = [
  "nextFinding",
  "prevFinding",
  "accept",
  "dismiss",
  "dismissWithReason",
  "nextPendingImage",
  "nextUnseenImage",
  "severity1",
  "severity2",
  "severity3",
  "severity4",
  "severity5",
] as const;

export type TriageActionId = (typeof TRIAGE_ACTIONS)[number];

/** action → bound chords (order = display order; any chord triggers). */
export type TriageKeymap = Record<TriageActionId, readonly string[]>;

export const DEFAULT_TRIAGE_KEYMAP: TriageKeymap = {
  nextFinding: ["j"],
  prevFinding: ["k"],
  accept: ["a", "Enter"],
  dismiss: ["x", "Delete"],
  dismissWithReason: ["Shift+x"],
  nextPendingImage: ["Space"],
  nextUnseenImage: ["Shift+Space"],
  severity1: ["1"],
  severity2: ["2"],
  severity3: ["3"],
  severity4: ["4"],
  severity5: ["5"],
};

/** Human labels for the Settings → Shortcuts list. */
export const TRIAGE_ACTION_LABELS: Record<TriageActionId, string> = {
  nextFinding: "Next finding (active image)",
  prevFinding: "Previous finding (active image)",
  accept: "Accept — promote the active finding",
  dismiss: "Dismiss the active finding (undoable)",
  dismissWithReason: "Dismiss with reason…",
  nextPendingImage: "Next image with pending findings",
  nextUnseenImage: "Next unseen image",
  severity1: "Set severity — grade 1",
  severity2: "Set severity — grade 2",
  severity3: "Set severity — grade 3",
  severity4: "Set severity — grade 4",
  severity5: "Set severity — grade 5",
};

/**
 * Chords that may never be (re)bound: surface-critical keys owned by
 * other layers. `d`/`r`/`p`/`l` belong to the DrawOverlay (draw-mode
 * toggle + mode hotkeys); arrows are the shared data-point navigation
 * (ANA-R3-NAV-01); Escape is the escape stack; Tab is focus traversal;
 * `?` is reserved for the shortcut overlay (IMP-07).
 */
export const RESERVED_CHORDS: ReadonlySet<string> = new Set([
  "Escape",
  "Tab",
  "Shift+Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "?",
  "d",
  "r",
  "p",
  "l",
]);

/**
 * Normalise a KeyboardEvent into a triage chord, or `null` when the
 * event can never be a triage chord:
 *   - Ctrl / Cmd / Alt held (reserved for browser + undo/redo),
 *   - a bare modifier keypress,
 *   - keys we cannot represent.
 *
 * Shift is encoded as a `Shift+` prefix EXCEPT for printable characters
 * whose identity already changes with Shift (e.g. `?`, `X` → we
 * lowercase letters so `Shift+x` stays distinguishable from `x`).
 */
export function eventToChord(
  e: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">,
): string | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const key = e.key;
  if (key === "Shift" || key === "Control" || key === "Alt" || key === "Meta") {
    return null;
  }
  let base: string;
  if (key === " " || key === "Spacebar") {
    base = "Space";
  } else if (key.length === 1) {
    // Letters lowercased so the chord is layout-stable; digits and
    // punctuation pass through.
    base = key.toLowerCase();
  } else {
    // Named keys (Enter, Delete, Backspace, Home, …) pass through.
    base = key;
  }
  return e.shiftKey ? `Shift+${base}` : base;
}

/** Display label for a chord (`Shift+x` → `Shift + X`, `Space` stays). */
export function chordLabel(chord: string): string {
  const shift = chord.startsWith("Shift+");
  const base = shift ? chord.slice("Shift+".length) : chord;
  const baseLabel = base.length === 1 ? base.toUpperCase() : base;
  return shift ? `Shift + ${baseLabel}` : baseLabel;
}

/**
 * Resolve a chord against a keymap. When a corrupt keymap holds the
 * same chord on two actions, the first action in canonical
 * `TRIAGE_ACTIONS` order wins (deterministic).
 */
export function resolveTriageAction(
  keymap: TriageKeymap,
  chord: string,
): TriageActionId | null {
  for (const action of TRIAGE_ACTIONS) {
    if (keymap[action].includes(chord)) return action;
  }
  return null;
}

export type RebindResult =
  | { ok: true; keymap: TriageKeymap }
  | { ok: false; reason: "reserved" }
  | { ok: false; reason: "conflict"; conflictsWith: TriageActionId };

/**
 * Replace binding slot `index` of `action` with `chord`.
 *
 * Conflict handling (kept deliberately simple — no silent stealing):
 *   - a RESERVED chord is rejected (`reason: "reserved"`),
 *   - a chord already bound to a DIFFERENT action is rejected with the
 *     owning action so the settings UI can say so,
 *   - re-assigning a chord the SAME action already holds is a no-op
 *     success (idempotent).
 */
export function rebindTriageKey(
  keymap: TriageKeymap,
  action: TriageActionId,
  index: number,
  chord: string,
): RebindResult {
  if (RESERVED_CHORDS.has(chord)) {
    return { ok: false, reason: "reserved" };
  }
  const owner = resolveTriageAction(keymap, chord);
  if (owner !== null && owner !== action) {
    return { ok: false, reason: "conflict", conflictsWith: owner };
  }
  const current = keymap[action];
  if (index < 0 || index >= current.length) {
    return { ok: true, keymap };
  }
  if (current[index] === chord) {
    return { ok: true, keymap };
  }
  // Same action, different slot, same chord (e.g. binding slot 1 to the
  // chord slot 0 already holds) — drop the duplicate from the other slot
  // by simply rejecting: it would leave a hole. Treat as no-op success.
  if (owner === action && current[index] !== chord) {
    return { ok: true, keymap };
  }
  const next = current.slice();
  next[index] = chord;
  return { ok: true, keymap: { ...keymap, [action]: next } };
}

/**
 * Lenient parse of a persisted keymap blob. Unknown actions are
 * stripped; missing actions fall back to defaults; non-string entries
 * and duplicate chords (across actions) are dropped — first owner in
 * canonical order wins, mirroring `resolveTriageAction`.
 */
export function parseTriageKeymap(raw: unknown): TriageKeymap {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return { ...DEFAULT_TRIAGE_KEYMAP };
  }
  const rec = raw as Record<string, unknown>;
  const out = {} as Record<TriageActionId, readonly string[]>;
  const seen = new Set<string>();
  for (const action of TRIAGE_ACTIONS) {
    const value = rec[action];
    let chords: string[];
    if (
      Array.isArray(value) &&
      value.every((c): c is string => typeof c === "string")
    ) {
      chords = value;
    } else {
      chords = [...DEFAULT_TRIAGE_KEYMAP[action]];
    }
    const deduped: string[] = [];
    for (const c of chords) {
      if (c.length === 0) continue;
      if (seen.has(c)) continue;
      if (RESERVED_CHORDS.has(c)) continue;
      seen.add(c);
      deduped.push(c);
    }
    out[action] = deduped;
  }
  return out;
}
